import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PortalHolding } from 'views/MyCellar/hooks/usePortalHoldings';
import { useTheme } from '../../../../context/ThemeContext';
import { ChevronDown } from '../../../../assets/icons';
import { getRegions, handleImageError } from '../../../../helpers';
import { toInternalId } from '../../../../utils';

// Helper to get wine image URL from lwin18
const getImageByLwin18 = (lwin18: string) => {
  return `https://cwcdst2prdctimgimprtfnct.blob.core.windows.net/height/400/${lwin18?.substring(0, 7)}.png`;
};

// Theme colors matching other page components
const lightThemeColors = {
  background: '#FFFFFF',
  cardBackground: '#F8FAFC',
  text: '#000000',
  textSecondary: '#666666',
  border: '#E2E8F0',
  positive: '#059669',
  negative: '#DC2626',
  dropdownBg: '#FFFFFF',
  dropdownHover: '#F1F5F9',
  dropdownSelected: '#3B82F6',
};

const darkThemeColors = {
  background: '#0A1628',
  cardBackground: '#0F1E32',
  text: '#FFFFFF',
  textSecondary: '#8B9CB3',
  border: '#1E3A50',
  positive: '#10B981',
  negative: '#EF4444',
  dropdownBg: '#1E293B',
  dropdownHover: '#334155',
  dropdownSelected: '#3B82F6',
};

type PeriodKey = 'totalPnl' | '1m' | '3m' | '6m' | '12m';

interface PeriodOption {
  key: PeriodKey;
  label: string;
  field: keyof PortalHolding | 'pnlPercent';
}

const periodOptions: PeriodOption[] = [
  { key: 'totalPnl', label: 'Total P&L %', field: 'pnlPercent' },
  { key: '1m', label: '1M % Change', field: 'changePercent1Month' },
  { key: '3m', label: '3M % Change', field: 'changePercent3Months' },
  { key: '6m', label: '6M % Change', field: 'changePercent6Months' },
  { key: '12m', label: '12M % Change', field: 'changePercent12Months' },
];

interface TopBottomPerformersProps {
  holdings: PortalHolding[];
  isLoading?: boolean;
  onItemClick?: (lwin18: string) => void;
}

const formatCompact = (value: number): string => {
  const absValue = Math.abs(value);
  if (absValue >= 1000000) {
    return `£${(value / 1000000).toFixed(1)}m`;
  }
  if (absValue >= 1000) {
    return `£${Math.round(value).toLocaleString('en-GB')}`;
  }
  return `£${value.toFixed(0)}`;
};

const TopBottomPerformers: FC<TopBottomPerformersProps> = ({ holdings, isLoading, onItemClick }) => {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const themeColors = isDark ? darkThemeColors : lightThemeColors;
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodKey>('totalPnl');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<{ side: 'top' | 'bottom'; index: number } | null>(null);

  const regionColors = useMemo(() => getRegions(t), [t]);

  const selectedOption = periodOptions.find((p) => p.key === selectedPeriod) || periodOptions[0];

  const { topPerformers, bottomPerformers } = useMemo(() => {
    if (!holdings || holdings.length === 0) {
      return { topPerformers: [], bottomPerformers: [] };
    }

    const getColorByRegion = (region?: string | null) => {
      return regionColors.find((x) => x.id === toInternalId(`${region}`))?.color ?? '#888888';
    };

    const getChangeValue = (h: PortalHolding): number => {
      if (selectedOption.field === 'pnlPercent') {
        return (h.pnlPercent || 0) * 100;
      }
      return (h[selectedOption.field] as number) || 0;
    };

    const sortedByChange = [...holdings]
      .filter((h) => {
        const val = getChangeValue(h);
        return val !== null && val !== undefined && !Number.isNaN(val);
      })
      .sort((a, b) => getChangeValue(b) - getChangeValue(a));

    const top = sortedByChange.slice(0, 5).map((h) => ({
      ...h,
      changeValue: getChangeValue(h),
      imageUrl: getImageByLwin18(h.lwin18),
      color: getColorByRegion(h.cultWinesAllocationRegion),
      criticScore: h.waScore || h.vScoreMedian || null,
      unitSize: h.unit || '',
    }));

    const bottom = sortedByChange
      .slice(-5)
      .reverse()
      .map((h) => ({
        ...h,
        changeValue: getChangeValue(h),
        imageUrl: getImageByLwin18(h.lwin18),
        color: getColorByRegion(h.cultWinesAllocationRegion),
        criticScore: h.waScore || h.vScoreMedian || null,
        unitSize: h.unit || '',
      }));

    return { topPerformers: top, bottomPerformers: bottom };
  }, [holdings, selectedOption, regionColors]);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {[0, 1].map((i) => (
          <div
            key={i}
            className="flex-1 flex flex-col rounded-lg overflow-hidden animate-pulse"
            style={{ backgroundColor: themeColors.background }}
          >
            <div className="px-5 pt-5 pb-3">
              <div className="h-6 w-40 rounded" style={{ backgroundColor: themeColors.cardBackground }} />
            </div>
            <div className="px-5 pb-5 space-y-3">
              {[1, 2, 3, 4, 5].map((j) => (
                <div key={j} className="h-14 rounded" style={{ backgroundColor: themeColors.cardBackground }} />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!holdings || holdings.length === 0) {
    return null;
  }

  const renderWineRow = (
    wine: PortalHolding & {
      changeValue: number;
      imageUrl: string;
      color: string;
      criticScore: number | null;
      unitSize: string;
    },
    index: number,
    isTop: boolean,
  ) => {
    const changeColor = wine.changeValue >= 0 ? themeColors.positive : themeColors.negative;
    const rankBgColor = isTop
      ? isDark
        ? 'rgba(16, 185, 129, 0.15)'
        : 'rgba(5, 150, 105, 0.1)'
      : isDark
      ? 'rgba(239, 68, 68, 0.15)'
      : 'rgba(220, 38, 38, 0.1)';
    const rankTextColor = isTop ? themeColors.positive : themeColors.negative;
    const isHovered = hoveredIndex?.side === (isTop ? 'top' : 'bottom') && hoveredIndex?.index === index;

    return (
      <div
        key={wine.lwin18}
        className="flex items-center gap-2 sm:gap-3 p-1.5 sm:p-2 rounded-lg cursor-pointer transition-all"
        style={{
          backgroundColor: isHovered ? themeColors.cardBackground : 'transparent',
        }}
        onMouseEnter={() => setHoveredIndex({ side: isTop ? 'top' : 'bottom', index })}
        onMouseLeave={() => setHoveredIndex(null)}
        onClick={() => onItemClick?.(wine.lwin18)}
      >
        {/* Rank Badge */}
        <div
          className="w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold flex-shrink-0"
          style={{ backgroundColor: rankBgColor, color: rankTextColor }}
        >
          {index + 1}
        </div>

        {/* Wine Image with colored bar */}
        <div className="flex-shrink-0 relative hidden md:block">
          <div className="w-1 h-12 absolute left-0 top-0 rounded-full" style={{ backgroundColor: wine.color }} />
          <img
            src={wine.imageUrl}
            alt={wine.wineName}
            className="w-12 h-12 object-contain ml-1.5"
            onError={handleImageError}
          />
        </div>

        {/* Wine Info */}
        <div className="flex-1 min-w-0">
          <div
            className="text-sm sm:text-base font-medium line-clamp-1 sm:line-clamp-2"
            style={{ color: themeColors.text }}
            title={wine.wineName}
          >
            {wine.wineName}
          </div>
          <div
            className="text-xs sm:text-sm flex items-center gap-1 sm:gap-2 flex-wrap"
            style={{ color: themeColors.textSecondary }}
          >
            <span className="truncate">
              {Math.floor(wine.vintage)} • {wine.cultWinesAllocationRegion || wine.region}
              <span className="hidden sm:inline">
                {' '}
                • x {wine.qty}
                {wine.unitSize ? ` • ${wine.unitSize}` : ''}
              </span>
            </span>
            {wine.criticScore && <span className="hidden md:inline">• {wine.criticScore} pts</span>}
          </div>
        </div>

        {/* Change & Value */}
        <div className="flex flex-col items-end flex-shrink-0 ml-1 sm:ml-2">
          <span className="text-sm sm:text-base " style={{ color: changeColor }}>
            {wine.changeValue >= 0 ? '+' : ''}
            {wine.changeValue.toFixed(1)}%
          </span>
          <span className="text-xs sm:text-sm hidden sm:block" style={{ color: themeColors.textSecondary }}>
            Mkt: {formatCompact(wine.totalValue)}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Top 5 Performers */}
      <div
        className="flex-1 flex flex-col rounded-lg overflow-hidden transition-colors"
        style={{ backgroundColor: themeColors.background }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-3 sm:px-5 pt-4 sm:pt-5 pb-3 sm:pb-4">
          <div className="flex items-center gap-1.5 sm:gap-2">
            <svg
              className="w-5 h-5 sm:w-6 sm:h-6"
              viewBox="0 0 24 24"
              fill="none"
              stroke={themeColors.positive}
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
            <h3 className="text-base sm:text-lg" style={{ color: themeColors.text }}>
              Top 5 Performers
            </h3>
          </div>

          {/* Dropdown */}
          <div className="relative">
            <button
              className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2 rounded-md text-sm sm:text-base border transition-colors"
              style={{
                backgroundColor: themeColors.cardBackground,
                borderColor: themeColors.border,
                color: themeColors.text,
              }}
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              <span className="hidden sm:inline">{selectedOption.label}</span>
              <span className="sm:hidden">
                {selectedOption.key === 'totalPnl' ? 'P&L' : selectedOption.key.toUpperCase()}
              </span>
              <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>

            {isDropdownOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setIsDropdownOpen(false)} />
                <div
                  className="absolute right-0 mt-1 w-40 sm:w-48 rounded-lg shadow-lg z-20 overflow-hidden"
                  style={{
                    backgroundColor: themeColors.dropdownBg,
                    border: `1px solid ${themeColors.border}`,
                  }}
                >
                  {periodOptions.map((option) => (
                    <button
                      key={option.key}
                      className="w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base text-left flex items-center gap-2 transition-colors"
                      style={{
                        backgroundColor: option.key === selectedPeriod ? themeColors.dropdownSelected : 'transparent',
                        color: option.key === selectedPeriod ? '#FFFFFF' : themeColors.text,
                      }}
                      onMouseEnter={(e) => {
                        if (option.key !== selectedPeriod) {
                          e.currentTarget.style.backgroundColor = themeColors.dropdownHover;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (option.key !== selectedPeriod) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                      onClick={() => {
                        setSelectedPeriod(option.key);
                        setIsDropdownOpen(false);
                      }}
                    >
                      {option.key === selectedPeriod && (
                        <svg
                          className="w-4 h-4 sm:w-5 sm:h-5"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                      <span className={option.key === selectedPeriod ? '' : 'ml-6 sm:ml-7'}>{option.label}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* List */}
        <div className="flex flex-col gap-0.5 sm:gap-1 px-2 sm:px-3 pb-3 sm:pb-4">
          {topPerformers.map((wine, index) => renderWineRow(wine, index, true))}
        </div>
      </div>

      {/* Bottom 5 Performers */}
      <div
        className="flex-1 flex flex-col rounded-lg overflow-hidden transition-colors"
        style={{ backgroundColor: themeColors.background }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-3 sm:px-5 pt-4 sm:pt-5 pb-3 sm:pb-4">
          <div className="flex items-center gap-1.5 sm:gap-2">
            <svg
              className="w-5 h-5 sm:w-6 sm:h-6"
              viewBox="0 0 24 24"
              fill="none"
              stroke={themeColors.negative}
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 17h8m0 0v-8m0 8l-8-8-4 4-6-6" />
            </svg>
            <h3 className="text-base sm:text-lg" style={{ color: themeColors.text }}>
              Bottom 5 Performers
            </h3>
          </div>

          {/* Period Label (follows left dropdown) */}
          <span className="text-sm sm:text-base" style={{ color: themeColors.textSecondary }}>
            <span className="hidden sm:inline">{selectedOption.label}</span>
            <span className="sm:hidden">
              {selectedOption.key === 'totalPnl' ? 'P&L' : selectedOption.key.toUpperCase()}
            </span>
          </span>
        </div>

        {/* List */}
        <div className="flex flex-col gap-0.5 sm:gap-1 px-2 sm:px-3 pb-3 sm:pb-4">
          {bottomPerformers.map((wine, index) => renderWineRow(wine, index, false))}
        </div>
      </div>
    </div>
  );
};

export default TopBottomPerformers;
